import { ActivityData, ActivityMatchSuggestion, ActivityMatchNotification, Workout, PlanRecalibrationSuggestion } from '../types/DataTypes';

/**
 * WorkoutMatchingService - Phase 3 Activity Tracking
 * 
 * Handles automatic matching of imported activities (Strava/Garmin) to scheduled workouts,
 * provides confidence scoring, user notifications, and plan recalibration suggestions.
 */
export class WorkoutMatchingService {
  
  /**
   * Analyze an imported activity and find potential workout matches
   */
  static analyzeActivityForMatches(
    activity: ActivityData, 
    scheduledWorkouts: Workout[]
  ): ActivityMatchSuggestion[] {
    const suggestions: ActivityMatchSuggestion[] = [];
    
    // Filter workouts to same day (+/- 1 day for timezone flexibility)
    const activityDate = new Date(activity.start);
    const relevantWorkouts = scheduledWorkouts.filter(workout => {
      if (!workout.scheduledDate) return false;
      
      const workoutDate = new Date(workout.scheduledDate);
      const dayDiff = Math.abs(activityDate.getTime() - workoutDate.getTime()) / (1000 * 60 * 60 * 24);
      return dayDiff <= 1.5; // Allow 1.5 days for timezone/scheduling flexibility
    });
    
    // Score each potential match
    for (const workout of relevantWorkouts) {
      const matchScore = this.calculateMatchScore(activity, workout);
      
      if (matchScore.confidence >= 0.3) { // Minimum threshold for suggestions
        suggestions.push({
          activity,
          workout,
          confidence: matchScore.confidence,
          reasons: matchScore.reasons,
          suggested_action: this.getSuggestedAction(matchScore.confidence)
        });
      }
    }
    
    // Sort by confidence (highest first)
    return suggestions.sort((a, b) => b.confidence - a.confidence);
  }
  
  /**
   * Calculate match confidence score between activity and workout
   */
  private static calculateMatchScore(
    activity: ActivityData, 
    workout: Workout
  ): { confidence: number; reasons: string[] } {
    let score = 0;
    const reasons: string[] = [];
    
    // Sport/Type matching (highest weight)
    const sportMatch = this.getSportMatchScore(activity.sport, workout.type);
    score += sportMatch.score * 0.4;
    if (sportMatch.matched) reasons.push(sportMatch.reason);
    
    // Duration matching (significant weight)
    const durationMatch = this.getDurationMatchScore(activity.duration_sec, workout.duration);
    score += durationMatch.score * 0.3;
    if (durationMatch.score > 0.5) reasons.push(durationMatch.reason);
    
    // Date/time proximity (moderate weight)
    const dateMatch = this.getDateMatchScore(activity.start, workout.scheduledDate);
    score += dateMatch.score * 0.2;
    if (dateMatch.score > 0.7) reasons.push(dateMatch.reason);
    
    // User pattern matching (lower weight but valuable)
    const patternMatch = this.getUserPatternScore(activity, workout);
    score += patternMatch.score * 0.1;
    if (patternMatch.score > 0.5) reasons.push(patternMatch.reason);
    
    return { confidence: Math.min(score, 1.0), reasons };
  }
  
  /**
   * Map activity sports to workout types and calculate match score
   */
  private static getSportMatchScore(
    activitySport: string, 
    workoutType: string
  ): { score: number; matched: boolean; reason: string } {
    const sportMappings: Record<string, string[]> = {
      'run': ['cardio', 'hiit'],
      'bike': ['cardio'],
      'swim': ['cardio'],
      'walk': ['cardio', 'recovery'],
      'hike': ['cardio', 'recovery'],
      'strength': ['strength'],
      'yoga': ['flexibility', 'recovery'],
      'workout': ['strength', 'hiit']
    };
    
    const activityTypes = sportMappings[activitySport.toLowerCase()] || [];
    
    if (activityTypes.includes(workoutType)) {
      return { 
        score: 1.0, 
        matched: true, 
        reason: `Perfect sport match: ${activitySport} activity matches ${workoutType} workout` 
      };
    }
    
    // Partial matches
    if (activitySport === 'run' && workoutType === 'strength') {
      return { 
        score: 0.3, 
        matched: false, 
        reason: `Possible cross-training: Running activity for strength day` 
      };
    }
    
    return { score: 0, matched: false, reason: 'Different activity types' };
  }
  
  /**
   * Calculate duration match score
   */
  private static getDurationMatchScore(
    activityDurationSec: number, 
    workoutDuration: string | undefined
  ): { score: number; reason: string } {
    // A workout with no planned length gives the match nothing to go on.
    if (!workoutDuration) return { score: 0, reason: 'Workout has no planned duration' };
    // Parse workout duration (e.g., "45 minutes", "1 hour")
    const durationMatch = workoutDuration.match(/(\d+)/);
    if (!durationMatch) return { score: 0, reason: 'Could not parse workout duration' };
    
    let workoutMinutes = parseInt(durationMatch[1]);
    if (workoutDuration.includes('hour')) workoutMinutes *= 60;
    
    const activityMinutes = activityDurationSec / 60;
    const durationDiff = Math.abs(activityMinutes - workoutMinutes);
    const durationRatio = durationDiff / workoutMinutes;
    
    if (durationRatio <= 0.2) { // Within 20%
      return { 
        score: 1.0, 
        reason: `Duration match: ${Math.round(activityMinutes)}min activity vs ${workoutMinutes}min workout` 
      };
    } else if (durationRatio <= 0.5) { // Within 50%
      return { 
        score: 0.7, 
        reason: `Similar duration: ${Math.round(activityMinutes)}min vs ${workoutMinutes}min planned` 
      };
    } else if (durationRatio <= 1.0) { // Within 100%
      return { 
        score: 0.4, 
        reason: `Different but reasonable duration difference` 
      };
    }
    
    return { score: 0.1, reason: 'Significant duration difference' };
  }
  
  /**
   * Calculate date/time proximity score
   */
  private static getDateMatchScore(
    activityStart: Date, 
    workoutScheduled?: Date
  ): { score: number; reason: string } {
    if (!workoutScheduled) return { score: 0.5, reason: 'No scheduled time to compare' };
    
    const timeDiff = Math.abs(activityStart.getTime() - workoutScheduled.getTime());
    const hoursDiff = timeDiff / (1000 * 60 * 60);
    
    if (hoursDiff <= 3) {
      return { score: 1.0, reason: 'Completed within 3 hours of scheduled time' };
    } else if (hoursDiff <= 12) {
      return { score: 0.8, reason: 'Completed same day as scheduled' };
    } else if (hoursDiff <= 24) {
      return { score: 0.6, reason: 'Completed within 24 hours of scheduled time' };
    }
    
    return { score: 0.3, reason: 'Completed on different day' };
  }
  
  /**
   * Calculate user pattern matching score
   */
  private static getUserPatternScore(
    activity: ActivityData, 
    workout: Workout
  ): { score: number; reason: string } {
    // This would analyze user's historical patterns
    // For now, return moderate confidence for same-day activities
    const sameDay = new Date(activity.start).toDateString() === 
                   new Date(workout.scheduledDate || '').toDateString();
    
    if (sameDay) {
      return { score: 0.7, reason: 'Completed on same day as scheduled' };
    }
    
    return { score: 0.2, reason: 'Different completion pattern' };
  }
  
  /**
   * Determine suggested action based on confidence
   */
  private static getSuggestedAction(confidence: number): 'auto_match' | 'ask_user' | 'create_standalone' {
    if (confidence >= 0.9) return 'auto_match';
    if (confidence >= 0.5) return 'ask_user';
    return 'create_standalone';
  }
  
  /**
   * Create notification for user decision
   */
  static createMatchNotification(
    activity: ActivityData,
    suggestions: ActivityMatchSuggestion[]
  ): ActivityMatchNotification {
    return {
      id: `notification_${Date.now()}`,
      type: 'match_suggestion',
      activity,
      suggestions,
      created_at: new Date(),
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
      status: 'pending'
    };
  }
  
  /**
   * Execute the match between activity and workout
   */
  static executeMatch(
    activity: ActivityData,
    workout: Workout,
    confidence: number,
    manual: boolean
  ): ActivityData {
    return {
      ...activity,
      workout_id: workout.id,
      status: 'matched',
      match_confidence: confidence,
      manual_match: manual,
      match_timestamp: new Date(),
      updated_at: new Date()
    };
  }
  
  /**
   * Analyze need for plan recalibration
   */
  static analyzePlanRecalibration(
    activity: ActivityData,
    plannedWorkout?: Workout
  ): PlanRecalibrationSuggestion | null {
    // Simple example - could be much more sophisticated
    if (!plannedWorkout) {
      // Standalone activity might indicate need for plan adjustment
      return {
        plan_id: 'current-plan',
        reason: 'User completed unplanned activity',
        suggested_changes: {
          workout_adjustments: [{
            workout_id: 'next-workout',
            current_config: {},
            suggested_config: { intensity: 'reduced' },
            reason: 'Account for additional unplanned activity'
          }]
        },
        confidence: 0.6,
        impact: 'minor'
      };
    }
    
    // Check if actual vs planned significantly different. A workout with no planned
    // length has nothing to compare the activity against.
    if (!plannedWorkout.duration) return null;
    const plannedDuration = this.parseDuration(plannedWorkout.duration);
    const actualDuration = activity.duration_sec / 60;
    const durationDiff = Math.abs(actualDuration - plannedDuration) / plannedDuration;
    
    if (durationDiff > 0.5) { // 50% difference
      return {
        plan_id: plannedWorkout.planId || 'current-plan',
        reason: 'Significant difference between planned and actual workout duration',
        suggested_changes: {
          intensity_modifications: [{
            workout_type: plannedWorkout.type,
            current_intensity: plannedWorkout.difficulty,
            suggested_intensity: actualDuration > plannedDuration ? 'harder' : 'easier',
            reason: `User ${actualDuration > plannedDuration ? 'exceeded' : 'fell short of'} planned duration by ${Math.round(durationDiff * 100)}%`
          }]
        },
        confidence: 0.8,
        impact: 'moderate'
      };
    }
    
    return null;
  }
  
  /**
   * Parse duration string to minutes
   */
  private static parseDuration(durationStr: string): number {
    const match = durationStr.match(/(\d+)/);
    if (!match) return 45; // default
    
    let minutes = parseInt(match[1]);
    if (durationStr.includes('hour')) minutes *= 60;
    
    return minutes;
  }

  /**
   * Process activity match acceptance - stub implementation
   */
  static processMatchAcceptance(
    _activityId: string, 
    _workoutId: string, 
    _suggestion: ActivityMatchSuggestion
  ): { success: boolean; autoCompleted?: boolean; message: string } {
    return {
      success: true,
      autoCompleted: true,
      message: 'Activity matched successfully'
    };
  }
} 